import { FC, ReactNode } from 'react';
import { Dialog } from 'radix-ui';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../../components/Typography';
import { TeamShirt } from '../../components/TeamShirt';
import { GameKeyPanel } from '../../components/GameKey';
import { ChevronUp, Info } from '../../components/svgs';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SubstituteRowState = 'active' | 'default';

export interface SubstituteRowData {
  /** Unique key for React rendering. */
  id: string;
  /** Player display name. */
  playerName: string;
  /** Row state controls right-side value + status cluster visibility. */
  state?: SubstituteRowState;
  /** Minute text shown only for active rows (e.g. "91'"). */
  minute?: string;
  /** Right column value for active rows. */
  points?: string | number;
  /** Right column value for default rows. */
  positionLabel?: string;
  /** Optional custom leading visual (shirt / flag / image). */
  leading?: ReactNode;
  /** Team key used by default TeamShirt when `leading` is not provided. */
  team?: string;
  /** Player position; goalkeepers render the GK-specific kit when `leading` is not provided. */
  position?: string;
  /** Optional press handler for the entire row. */
  onPress?: () => void;
}

export interface SubstitutesCardProps {
  rows?: SubstituteRowData[];
  title?: string;
  howToScoreLabel?: string;
  onHowToScore?: () => void;
  closeLabel?: string;
  onClose?: () => void;
  /**
   * Optional content rendered inside the expanded Game Key section.
   * Keep undefined when you only need the collapsed card.
   */
  gameKeyContent?: ReactNode;
  gameKeyExpanded?: boolean;
  onGameKeyExpandedChange?: (expanded: boolean) => void;
  className?: string;
}

export interface SubstitutesModalProps extends Omit<SubstitutesCardProps, 'className'> {
  /** Controlled open state. Omit for uncontrolled usage. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element wrapped with `Dialog.Trigger asChild`. */
  trigger?: ReactNode;
  className?: string;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_SUBSTITUTE_ROWS: SubstituteRowData[] = [
  { id: '1', playerName: 'Player name', state: 'active', minute: "91'", points: 2 },
  { id: '2', playerName: 'Player name', state: 'active', minute: "91'", points: 2 },
  { id: '3', playerName: 'Player name', state: 'active', minute: "91'", points: 1 },
  { id: '4', playerName: 'Player name', state: 'active', minute: "91'", points: 1 },
  { id: '5', playerName: 'Player name', state: 'active', minute: "91'", points: 1 },
  { id: '6', playerName: 'Player name', state: 'default', positionLabel: 'POS' },
  { id: '7', playerName: 'Player name', state: 'default', positionLabel: 'POS' },
  { id: '8', playerName: 'Player name', state: 'default', positionLabel: 'POS' },
  { id: '9', playerName: 'Player name', state: 'default', positionLabel: 'POS' },
];

// ─── Row helpers ──────────────────────────────────────────────────────────────

const SubOnIndicator: FC = () => (
  <span className="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-[#00a651]">
    <ChevronUp className="size-[10px] text-white" aria-hidden />
  </span>
);

const SubLeadingVisual: FC<{ row: SubstituteRowData }> = ({ row }) => {
  if (row.leading) {
    return <div className="size-8 shrink-0 overflow-hidden">{row.leading}</div>;
  }

  return (
    <TeamShirt
      team={row.team ?? 'placeholder'}
      position={row.position}
      className="!size-8 shrink-0 [&>img]:w-full [&>img]:h-full [&>img]:object-contain"
    />
  );
};

const SubstitutesRow: FC<{ row: SubstituteRowData }> = ({ row }) => {
  const state = row.state ?? 'default';
  const isActive = state === 'active';
  const rightValue = isActive ? (row.points ?? 0) : (row.positionLabel ?? 'POS');

  const content = (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <SubLeadingVisual row={row} />

        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Typography
            as="span"
            variant="subheadline010"
            className="truncate text-[#222526] text-xs"
          >
            {row.playerName}
          </Typography>

          {isActive && (
            <div className="flex shrink-0 items-center gap-2">
              <SubOnIndicator />
              <Typography as="span" variant="paragraph010" className="text-[#222526] text-xs">
                {row.minute ?? "91'"}
              </Typography>
            </div>
          )}
        </div>
      </div>

      <Typography
        as="span"
        variant="subheadline020"
        className="w-9 text-[14px] shrink-0 text-center text-[#222526]"
      >
        {rightValue}
      </Typography>
    </>
  );

  if (row.onPress) {
    return (
      <button
        type="button"
        onClick={row.onPress}
        className="flex w-full items-center gap-2 bg-white px-4 py-3 text-left hover:bg-[#f9f9fb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#030033]"
      >
        {content}
      </button>
    );
  }

  return <div className="flex w-full items-center gap-2 bg-white px-4 py-3">{content}</div>;
};

// ─── Card (pure UI, no Dialog) ───────────────────────────────────────────────

export const SubstitutesCard: FC<SubstitutesCardProps> = ({
  rows = DEFAULT_SUBSTITUTE_ROWS,
  title = 'SUBS',
  howToScoreLabel = 'How To Score',
  onHowToScore,
  closeLabel = 'Close',
  onClose,
  gameKeyContent,
  gameKeyExpanded,
  onGameKeyExpandedChange,
  className,
}) => (
  <div className={twMerge('flex w-full flex-col gap-2', className)}>
    <div
      className={twMerge(
        'w-full overflow-hidden rounded-[12px] bg-white',
        'shadow-[0px_4px_40px_0px_rgba(34,37,38,0.2)]'
      )}
    >
      <div className="flex max-h-[68px] items-center justify-between border-b border-[#eae9fd] px-4 py-5">
        <Typography as="h2" variant="label050" className="text-[#222526] !font-[800]">
          {title}
        </Typography>

        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-2 rounded-[4px] hover:opacity-70 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#030033]"
            aria-label={closeLabel}
          >
            <Typography as="span" variant="button020" className="text-[#222526]">
              {closeLabel}
            </Typography>
          </button>
        ) : onHowToScore ? (
          <button
            type="button"
            onClick={onHowToScore}
            className="flex items-center gap-2 rounded-[4px] hover:opacity-70 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#030033]"
            aria-label={howToScoreLabel}
          >
            <Typography as="span" variant="button020" className="text-[#222526]">
              {howToScoreLabel}
            </Typography>
            <Info className="size-6 text-[#222526]" aria-hidden />
          </button>
        ) : null}
      </div>

      <div
        className={`${gameKeyExpanded ? 'max-h-[240px]' : 'max-h-[336px]'} flex flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-[--color-neutral-300] scrollbar-track-transparent scrollbar-thumb-rounded-full`}
      >
        {rows.map(row => (
          <SubstitutesRow key={row.id} row={row} />
        ))}
      </div>
    </div>

    <GameKeyPanel
      expanded={gameKeyExpanded}
      onExpandedChange={onGameKeyExpandedChange}
      className="w-full"
    >
      {gameKeyContent}
    </GameKeyPanel>
  </div>
);

// ─── Dialog wrapper ───────────────────────────────────────────────────────────

export const SubstitutesModal: FC<SubstitutesModalProps> = ({
  open,
  onOpenChange,
  trigger,
  className,
  ...cardProps
}) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}

    <Dialog.Portal>
      <Dialog.Overlay
        className={twMerge(
          'fixed inset-0 z-50 bg-black/50',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
        )}
      />

      <Dialog.Content
        className={twMerge(
          'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
          'w-[411px] max-w-[calc(100vw-32px)]',
          'max-h-[calc(100dvh-32px)] overflow-y-auto',
          'focus:outline-none',
          className
        )}
        aria-describedby={undefined}
      >
        <Dialog.Title className="sr-only">SUBS</Dialog.Title>
        <SubstitutesCard {...cardProps} />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);
